import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useSession } from '../contexts/SessionContext';
import { get } from '../utils/api';
import { searchCachedProducts } from '../utils/cacheWarm';
import { debounce } from 'lodash';
import PageLayout from './common/PageLayout';
import { useKeyboard } from '../context/KeyboardContext';
import ProductVariationDialog from './ProductVariationDialog';
import { getLineChargeTotal } from '../utils/cartPricing';

// Material-UI Components
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Container,
  Grid,
  TextField,
  Typography,
  CircularProgress,
  InputAdornment,
  IconButton,
  Snackbar,
  Alert,
  Chip,
  Collapse,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  ListItemIcon,
  Paper,
  Badge,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent
} from '@mui/material';
import { 
  Search as SearchIcon, 
  Add as AddIcon, 
  Remove as RemoveIcon,
  Cached as CachedIcon, 
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ShoppingCart as ShoppingCartIcon,
  Home as HomeIcon,
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
  Backspace as BackspaceIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import _ from 'lodash';

const ProductsPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [expandedProducts, setExpandedProducts] = useState({});
  const [quantities, setQuantities] = useState({});
  // Quick Add state
  const [quickAddRows, setQuickAddRows] = useState([]); // array of arrays of product IDs
  const [quickAddProductsMap, setQuickAddProductsMap] = useState({}); // id -> product
  const [quickQuantities, setQuickQuantities] = useState({}); // id -> qty
  // Quick Select (upcoming calendar events) state
  const [primeIds, setPrimeIds] = useState([]); // flat array of product IDs
  const [primeProductsMap, setPrimeProductsMap] = useState({}); // id -> product
  const [primeQuantities, setPrimeQuantities] = useState({}); // id -> qty
  const [variationDialogOpen, setVariationDialogOpen] = useState(false);
  const [dialogProduct, setDialogProduct] = useState(null);
  const { addToCart, removeFromCart, cart } = useCart();
  const { session, setLastOrigin } = useSession();
  const navigate = useNavigate();
  const [showCheckoutButton, setShowCheckoutButton] = useState(false);
  const kb = useKeyboard();
  const detailsIframeRef = useRef(null);
  // Details dialog state
  const [detailsDialog, setDetailsDialog] = useState({ open: false, url: '', title: '', loading: false });
  // Image dialog state (for viewing product thumbnails)
  const [imageDialog, setImageDialog] = useState({ open: false, src: '', title: '', product: null });
  // Floating action after add-to-cart
  const [faOpen, setFaOpen] = useState(false);
  const [faAnchorEl, setFaAnchorEl] = useState(null);
  const faTimerRef = useRef(null);

  // Derive cart totals for floating checkout label
  const totalItems = Array.isArray(cart)
    ? cart.reduce((sum, item) => sum + (parseInt(item.quantity) || 1), 0)
    : 0;
  const totalAmount = Array.isArray(cart)
    ? cart.reduce((sum, item) => sum + getLineChargeTotal(item), 0)
    : 0;
  // Stash the element that opened the variations dialog so we can anchor the popover to it
  const faAnchorStashRef = useRef(null);

  // Remember origin as 'products' when this page mounts
  useEffect(() => {
    try { setLastOrigin && setLastOrigin('products'); } catch {}
  }, [setLastOrigin]);

  // Show snackbar notification
  const showSnackbar = useCallback((message, severity = 'info') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  }, []);

  // Fetch Quick Select product IDs from upcoming calendar events (returns { prime_products: "1,2,3" })
  useEffect(() => {
    const loadPrime = async () => {
      try {
        const resp = await get('/api/kiosk-prime-products');
        const csv = String(resp?.prime_products || '').trim();
        if (!csv) {
          setPrimeIds([]);
          setPrimeProductsMap({});
          setPrimeQuantities({});
          return;
        }
        const ids = Array.from(new Set(csv.split(',').map(s => parseInt(String(s).trim(), 10)).filter(n => !isNaN(n))));
        if (ids.length === 0) {
          setPrimeIds([]);
          setPrimeProductsMap({});
          setPrimeQuantities({});
          return;
        }
        setPrimeIds(ids);
        const query = ids.join(',');
        const prodResp = await get(`/api/products?productId=${encodeURIComponent(query)}`);
        const list = Array.isArray(prodResp?.products)
          ? prodResp.products
          : (Array.isArray(prodResp?.data) ? prodResp.data : []);
        const map = {};
        list.forEach(p => { if (p && p.id != null) map[p.id] = p; });
        setPrimeProductsMap(map);
        const q = {};
        ids.forEach(id => { q[id] = 1; });
        setPrimeQuantities(q);
      } catch (e) {
        console.error('Failed to load prime products:', e);
      }
    };
    loadPrime();
  }, []);

  // Handle snackbar close
  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Render Quick Select section (upcoming events with mapped products)
  const renderPrimePicks = () => {
    if (!primeIds || primeIds.length === 0) return null;

    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          mt: 3,
          bgcolor: '#fff3e0',
          border: '1px solid #ffe0b2',
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center'
        }}
      >
        <Typography variant="h6" gutterBottom sx={{ textAlign: 'center', width: '100%' }}>
          Quick Select
        </Typography>
        <Box
          sx={{
            display: primeIds.length > 4 ? 'flex' : 'grid',
            gridTemplateColumns: primeIds.length <= 4 ? 'repeat(4, minmax(0, 1fr))' : 'none',
            gap: 2.5,
            mb: 1,
            width: '100%',
            alignItems: 'stretch',
            justifyItems: primeIds.length <= 4 ? 'center' : 'stretch',
            justifyContent: primeIds.length > 4 ? 'flex-start' : 'center',
            overflowX: primeIds.length > 4 ? 'auto' : 'visible',
            overflowY: 'hidden'
          }}
        >
          {primeIds.map((id) => {
            const p = primeProductsMap[id];
            if (!p) return null;
            const isVariable = p.type === 'variable';
            const imageUrl = p.image || p.images?.[0]?.src || '';
            return (
              <Paper
                key={`prime-${id}`}
                elevation={1}
                sx={{
                  p: 2,
                  minWidth: 180,
                  maxWidth: 200,
                  width: primeIds.length > 4 ? 180 : '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center'
                }}
              >
                <Box sx={{
                  width: 100,
                  height: 100,
                  bgcolor: '#f5f5f5',
                  borderRadius: 1,
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 1.25,
                  cursor: imageUrl ? 'pointer' : 'default',
                }}
                onClick={() => { if (imageUrl) openImage(p, imageUrl); }}
                >
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt={p.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        padding: 6
                      }}
                    />
                  ) : (
                    <Typography variant="caption" color="text.secondary" sx={{ p: 1 }}>No image</Typography>
                  )}
                </Box>
                <Typography variant="subtitle2" noWrap title={p.name} sx={{ width: '100%', mb: 1 }}>
                  {p.name}
                </Typography>
                {!isVariable && p.price && (
                  <Typography variant="body2" color="primary" sx={{ fontWeight: 600, mb: 1 }}>
                    {formatPrice(p.price)}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                  <Button
                    variant="contained"
                    size="medium"
                    onClick={(e) => isVariable ? openVariationDialog(p, e.currentTarget) : handlePrimeAddSimple(p, e.currentTarget)}
                    sx={{ flex: 1 }}
                  >
                    Add to Cart
                  </Button>
                  {/* Details button removed as requested */}
                </Box>
              </Paper>
            );
          })}
        </Box>
      </Paper>
    );
  };

  // Open details dialog for a product if permalink exists
  const openDetails = (product) => {
    const url = product?.permalink || product?.url || '';
    if (!url) return;
    setDetailsDialog({ open: true, url, title: product?.name || 'Details', loading: true });
  };
  const closeDetails = () => setDetailsDialog(prev => ({ ...prev, open: false }));

  // Open a plain image dialog from a product thumbnail
  const openImage = (product, fallbackSrc) => {
    const fullImageUrl = product?.full_image || product?.image || product?.images?.[0]?.src || fallbackSrc || '';
    if (!fullImageUrl) return;
    setImageDialog({ open: true, src: fullImageUrl, title: product?.name || 'Image', product });
  };
  const closeImage = () => {
    setImageDialog(prev => ({ ...prev, open: false }));
  };

  // Add simple product directly from image dialog
  const handleAddSimpleFromImage = (product) => {
    if (!product) return;
    const cartItemId = `${product.id}-0`;
    const imageUrl = product.image || product.images?.[0]?.src || imageDialog.src || '';
    addToCart({
      id: cartItemId,
      cart_item_id: cartItemId,
      name: product.name,
      price: parseFloat(product.price) || 0,
      quantity: 1,
      product_id: product.id,
      variation_id: 0,
      image: imageUrl,
      sku: product.sku || ''
    });
    setShowCheckoutButton(true);
  };

  // Render Quick Add section (env-configured; excludes Quick Select ids to avoid duplicates)
  const renderQuickAdd = () => {
    if (!quickAddRows || quickAddRows.length === 0) return null;
    
    // Normalize to numbers to avoid '1' vs 1 mismatches
    const primeSet = new Set((primeIds || []).map((n) => Number(n)));
    const filteredRows = quickAddRows
      .map(row => row.filter(id => !primeSet.has(Number(id))))
      .filter(row => row.length > 0);

    if (filteredRows.length === 0) return null;

    const renderRow = (ids, rowIdx) => (
      <Box
        key={`qa-row-${rowIdx}`}
        sx={{
          display: 'flex',
          gap: 2.5,
          mb: 2,
          overflowX: 'auto',
          justifyContent: 'center',
          width: '100%'
        }}
      >
        {ids.map((id) => {
          const p = quickAddProductsMap[id];
          if (!p) return null;
          const isVariable = p.type === 'variable';
          const imageUrl = p.image || p.images?.[0]?.src || '';
          return (
            <Paper
              key={id}
              elevation={1}
              sx={{
                p: 2,
                minWidth: 180,
                maxWidth: 200,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center'
              }}
            >
              <Box sx={{
                width: 100,
                height: 100,
                bgcolor: '#f5f5f5',
                borderRadius: 1,
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 1.25,
                cursor: imageUrl ? 'pointer' : 'default'
              }}
              onClick={() => {
                if (imageUrl) openImage(p, imageUrl);
              }}
              >
                {imageUrl ? (
                  <img src={imageUrl} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 6 }} />
                ) : (
                  <Typography variant="caption" color="text.secondary" sx={{ p: 1 }}>No image</Typography>
                )}
              </Box>
              <Typography variant="subtitle2" noWrap title={p.name} sx={{ width: '100%', mb: 1 }}>
                {p.name}
              </Typography>
              {!isVariable && p.price && (
                <Typography variant="body2" color="primary" sx={{ fontWeight: 600, mb: 1 }}>
                  {formatPrice(p.price)}
                </Typography>
              )}
              <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                <Button
                  variant="contained"
                  size="medium"
                  onClick={(e) => isVariable ? openVariationDialog(p, e.currentTarget) : handleQuickAddSimple(p, e.currentTarget)}
                  sx={{ flex: 1 }}
                >
                  Add to Cart
                </Button>
              </Box>
            </Paper>
          );
        })}
      </Box>
    );

    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          mt: 3,
          bgcolor: '#f3e5f5',
          border: '1px solid #e1bee7',
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center'
        }}
      >
        <Typography id="quick-add-label" variant="h6" gutterBottom sx={{ textAlign: 'center', width: '100%' }}>
          Quick Add
        </Typography>
        {filteredRows.map((ids, idx) => renderRow(ids, idx))}
      </Paper>
    );
  };

  // Quick add handlers
  const openVariationDialog = (product, anchorEl) => {
    setDialogProduct(product);
    // Stash the opener element to anchor the floating action popover after add
    faAnchorStashRef.current = anchorEl || null;
    setVariationDialogOpen(true);
  };

  const closeVariationDialog = () => {
    setVariationDialogOpen(false);
    setDialogProduct(null);
  };

  const handleQuickAddSimple = (product, anchorEl) => {
    const qty = Math.max(1, parseInt(quickQuantities[product.id]) || 1);
    const cartItemId = `${product.id}-0`;
    const imageUrl = product.image || product.images?.[0]?.src || '';
    addToCart({
      id: cartItemId,
      cart_item_id: cartItemId,
      name: product.name,
      price: parseFloat(product.price) || 0,
      quantity: qty,
      product_id: product.id,
      variation_id: 0,
      image: imageUrl,
      sku: product.sku || ''
    });
    setShowCheckoutButton(true);
    // Popover deprecated; do not open it
    setFaOpen(false);
    setFaAnchorEl(null);
    if (faTimerRef.current) clearTimeout(faTimerRef.current);
  };

  // Prime simple add (uses primeQuantities store)
  const handlePrimeAddSimple = (product, anchorEl) => {
    const qty = Math.max(1, parseInt(primeQuantities[product.id]) || 1);
    const cartItemId = `${product.id}-0`;
    const imageUrl = product.image || product.images?.[0]?.src || '';
    addToCart({
      id: cartItemId,
      cart_item_id: cartItemId,
      name: product.name,
      price: parseFloat(product.price) || 0,
      quantity: qty,
      product_id: product.id,
      variation_id: 0,
      image: imageUrl,
      sku: product.sku || ''
    });
    setShowCheckoutButton(true);
    // Popover deprecated; do not open it
    setFaOpen(false);
    setFaAnchorEl(null);
    if (faTimerRef.current) clearTimeout(faTimerRef.current);
  };

  const onDialogAddToCart = (item) => {
    addToCart(item);
    setShowCheckoutButton(true);
    // Popover deprecated; rely on floating Checkout FAB
    setFaOpen(false);
    setFaAnchorEl(null);
    faAnchorStashRef.current = null;
    if (faTimerRef.current) clearTimeout(faTimerRef.current);
  };

  // Fetch products from API
  const fetchProducts = useCallback(async (searchTerm = '') => {
    try {
      setLoading(true);
      const endpoint = searchTerm 
        ? `/api/products?search=${encodeURIComponent(searchTerm)}` 
        : '/api/products';
      
      console.log('🔍 Fetching products from:', endpoint);
      const response = await get(endpoint);
      console.log('📦 API Response:', response);
      
      // Check if response has data array
      if (response && response.data && Array.isArray(response.data)) {
        console.log(`✅ Found ${response.data.length} products`);
        setProducts(response.data);
      } else {
        console.warn('⚠️ Unexpected response format, falling back to empty array');
        setProducts([]);
      }
      setError('');
    } catch (err) {
      console.error('Error fetching products:', err);
      // Live fetch failed (e.g. network blip) → fall back to the cached catalog snapshot.
      const cached = searchCachedProducts(searchTerm);
      if (Array.isArray(cached) && cached.length > 0) {
        console.warn(`Products live fetch failed; showing ${cached.length} from cached catalog`);
        setProducts(cached);
        setError('');
      } else {
        setProducts([]);
        setError('Please wait while we are loading the information');
        showSnackbar('Loading product information, please wait...', 'info');
      }
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  // Initial data fetch
  useEffect(() => {
    fetchProducts();
    
    // Cleanup function
    return () => {
      // Cleanup code if needed
    };
  }, [fetchProducts]);

  // Parse quick add env and fetch products by IDs
  useEffect(() => {
    const env = process.env.REACT_APP_QUICK_ADD_PRODUCTS || '';
    if (!env.trim()) return;

    const rows = env.split('|')
      .map(row => row.split(',')
        .map(s => parseInt(String(s).trim(), 10))
        .filter(n => !isNaN(n))
      )
      .filter(arr => arr.length > 0);

    if (rows.length === 0) return;
    setQuickAddRows(rows);

    const allIds = Array.from(new Set(rows.flat()));
    const query = allIds.join(',');

    const loadQuickAdd = async () => {
      try {
        const resp = await get(`/api/products?productId=${encodeURIComponent(query)}`);
        const list = Array.isArray(resp?.products)
          ? resp.products
          : (Array.isArray(resp?.data) ? resp.data : []);
        const map = {};
        list.forEach(p => { map[p.id] = p; });
        setQuickAddProductsMap(map);
        // initialize quick quantities to 1
        const q = {};
        allIds.forEach(id => { q[id] = 1; });
        setQuickQuantities(q);
      } catch (e) {
        console.error('Failed to load quick add products:', e);
      }
    };

    loadQuickAdd();
  }, []);

  // Refresh products from the API
  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await fetchProducts(searchTerm);
      showSnackbar('Products refreshed successfully', 'success');
    } catch (err) {
      console.error('Refresh error:', err);
      showSnackbar('Error refreshing products', 'error');
    } finally {
      setRefreshing(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchProducts();
    
    // Cleanup debounce on unmount
    return () => {
      debouncedSearchRef.cancel();
    };
  }, []);

  // Toggle product expansion
  const toggleProductExpansion = (productId) => {
    setExpandedProducts(prev => ({
      ...prev,
      [productId]: !prev[productId]
    }));
  };

  // Get products to display - either filtered by search or all products
  const displayedProducts = searchTerm.trim() ? products.filter(product => {
    const term = searchTerm.toLowerCase().trim();
    const nameMatch = product.name && product.name.toLowerCase().includes(term);
    const skuMatch = product.sku && product.sku.toLowerCase().includes(term);
    const categoryMatch = product.categories && product.categories.some(cat => 
      cat && cat.name && cat.name.toLowerCase().includes(term)
    );
    
    return nameMatch || skuMatch || categoryMatch;
  }) : products;

  // Create a ref for the debounced search function
  const debouncedSearchRef = useRef(
    _.debounce((term) => {
      fetchProducts(term);
    }, 300)
  ).current;

  // Handle search input change
  const handleSearchChange = (e) => {
    const term = e.target.value;
    console.log('🔍 Search term changed:', term);
    setSearchTerm(term);
    
    // Debounce the search to avoid too many API calls
    if (term.trim()) {
      console.log('🔍 Triggering search for:', term);
      debouncedSearchRef(term);
    } else {
      console.log('🔄 Resetting to show all products');
      fetchProducts('');
    }
  };

  // Open virtual keyboard for search
  const openSearchKeyboard = useCallback(() => {
    if (!kb) return;
    // If virtual keyboard is disabled, allow physical keyboard input to work normally
    if (kb.keyboardDisabled) {
      console.log('[ProductsPage] Virtual keyboard disabled, physical keyboard will work normally');
      return;
    }
    kb.open('alpha', {
      showEnter: true,
      // Place the keyboard at the center of the lower half of the screen
      position: 'lowerCenter',
      hCenter: true,
      widthPct: 0.85,
      heightVh: 35,
      autoHeight: true
    }, {
      onInput: (ch) => {
        setSearchTerm((prev) => {
          const next = (prev || '') + ch;
          if (next.trim()) {
            debouncedSearchRef(next);
          } else {
            fetchProducts('');
          }
          return next;
        });
      },
      onBackspace: () => {
        setSearchTerm((prev) => {
          const next = (prev || '') .slice(0, -1);
          if (next.trim()) {
            debouncedSearchRef(next);
          } else {
            fetchProducts('');
          }
          return next;
        });
      },
      onEnter: () => {
        // Close keyboard only; predictive search results are already visible
        kb.close();
      },
      onClear: () => {
        setSearchTerm('');
        fetchProducts('');
      }
    });
  }, [kb, debouncedSearchRef, fetchProducts, searchTerm]);

  // Handle quantity change
  const handleQuantityChange = (productId, variationId, eventOrValue) => {
    try {
      // Handle both direct value and event object
      const value = typeof eventOrValue === 'object' ? eventOrValue.target.value : eventOrValue;
      const quantity = Math.max(1, Math.min(100, parseInt(value) || 1));
      
      // Update the local quantities state
      const quantityKey = variationId ? `${productId}-${variationId}` : `${productId}-0`;
      setQuantities(prev => ({
        ...prev,
        [quantityKey]: quantity
      }));
      
      // If the item is in the cart, update its quantity
      const cartItem = cart.find(item => 
        item.product_id == productId && 
        (variationId ? item.variation_id == variationId : !item.variation_id)
      );
      
      if (cartItem) {
        updateQuantity(cartItem.id, quantity);
      }
    } catch (error) {
      console.error('Error updating quantity:', error);
    }
  };

  // Check if a product/variation is in the cart
  const isInCart = (product, variation = null) => {
    const variationId = variation ? variation.id : 0;
    return cart.some(item => 
      item.product_id === product.id && 
      (variationId === 0 ? !item.variation_id || item.variation_id === 0 : item.variation_id === variationId)
    );
  };

  // Toggle item in cart (add/remove)
  const toggleCartItem = (product, variation = null, anchorEl = null) => {
    // Create a unique cart item ID combining product and variation IDs
    const cartItemId = variation ? `${product.id}-${variation.id}` : `${product.id}-0`;
    const quantityKey = variation ? `${product.id}-${variation.id}` : `${product.id}-0`;
    const quantity = quantities[quantityKey] || 1;
    
    // Format the display name
    let displayName = product.name;
    let productImage = product.image
    if (variation) {
      const variationName = variation.attributes
        ? Object.values(variation.attributes).map(attr => attr.option).join(', ')
        : variation.name || 'Selected Option';
      displayName = `${product.name} - ${variationName}`;
    }
    
    if (isInCart(product, variation)) {
      // Remove from cart using the cartItemId
      removeFromCart(cartItemId);
    } else {
      // Add to cart with proper structure
      addToCart({
        id: cartItemId, // Unique ID for the cart item
        cart_item_id: cartItemId, // Also store as cart_item_id for consistency
        name: displayName,
        price: parseFloat(variation ? variation.price : product.price),
        quantity: quantity,
        product_id: product.id, // Store product ID separately
        variation_id: variation ? variation.id : 0, // Store variation ID separately (0 for simple products)
        image: productImage,
        sku: variation ? variation.sku : product.sku || ''
      });
      setShowCheckoutButton(true);
      try { setFaAnchorEl(anchorEl || null); setFaOpen(true); } catch {}
      if (faTimerRef.current) clearTimeout(faTimerRef.current);
      faTimerRef.current = setTimeout(() => setFaOpen(false), 10000);
    }
  };

  // Format price
  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(price);
  };

  // Get price range for products with variations
  const getPriceRange = (product) => {
    if (!hasVariations(product) || !product.variations || product.variations.length === 0) {
      return formatPrice(product.price);
    }

    // Get all variation prices
    const prices = product.variations
      .map(v => parseFloat(v.price))
      .filter(price => !isNaN(price));

    if (prices.length === 0) return 'Price not available';
    
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    
    if (minPrice === maxPrice) {
      return formatPrice(minPrice);
    }
    
    return `${formatPrice(minPrice)} - ${formatPrice(maxPrice)}`;
  };

  // Get product category
  const getProductCategory = (product) => {
    return product.categories?.[0]?.name || 'Uncategorized';
  };

  // Check if product has variations
  const hasVariations = (product) => {
    return product.type === 'variable' && product.variations && product.variations.length > 0;
  };

  // Format variation name by replacing hyphens with spaces and capitalizing first letters
  const formatVariationName = (name) => {
    if (!name) return '';
    // Replace hyphens with spaces and trim any extra spaces
    const withSpaces = name.replace(/-/g, ' ').replace(/\s+/g, ' ').trim();
    // Capitalize first letter of each word
    return withSpaces
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Render product variations
  const renderVariations = (product) => {
    if (!hasVariations(product)) return null;

    return (
      <Collapse in={expandedProducts[product.id]} timeout="auto" unmountOnExit>
        <List dense disablePadding>
          {product.variations.map((variation) => {
            const displayName = formatVariationName(variation.name);
            return (
              <React.Fragment key={variation.id}>
                <Divider />
                <ListItem 
                  secondaryAction={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {parseFloat(variation.price) === 1 && (
                        <TextField
                          type="number"
                          size="small"
                          value={quantities[`${product.id}-${variation.id}`] || 1}
                          onChange={(e) => handleQuantityChange(product.id, variation.id, e.target.value)}
                          inputProps={{ 
                            min: 1, 
                            max: 100,
                            style: { 
                              width: '60px',
                              textAlign: 'center',
                              padding: '8px 4px',
                              WebkitAppearance: 'none',
                              margin: 0
                            }
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '& input': {
                                padding: '8px 4px',
                                textAlign: 'center'
                              }
                            }
                          }}
                        />
                      )}
                      <Button
                        variant={isInCart(product, variation) ? 'outlined' : 'contained'}
                        color={isInCart(product, variation) ? 'secondary' : 'primary'}
                        size="small"
                        startIcon={isInCart(product, variation) ? <RemoveIcon /> : <AddIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleCartItem(product, variation, e.currentTarget);
                        }}
                        sx={{
                          minWidth: '100px',
                          '&:hover': {
                            bgcolor: isInCart(product, variation) ? 'action.hover' : 'primary.dark'
                          }
                        }}
                      >
                        {isInCart(product, variation) ? 'Remove' : 'Add to Cart'}
                      </Button>
                    </Box>
                  } 
                  disablePadding
                >
                  <ListItemButton
                    onClick={(e) => {
                      try { e.stopPropagation(); } catch {}
                      toggleCartItem(product, variation, e.currentTarget);
                    }}
                  >
                    <ListItemText 
                      primary={displayName} 
                      secondary={formatPrice(variation.price)}
                      sx={{ ml: 2 }}
                    />
                  </ListItemButton>
                </ListItem>
              </React.Fragment>
            );
          })}
        </List>
      </Collapse>
    );
  };

  // Render product list item
  const renderProductItem = (product) => {
    const isExpanded = !!expandedProducts[product.id];
    const variations = hasVariations(product);
    const hasImage = product.images?.[0]?.src;
    
    // Image data available in product.images
    return (
      <React.Fragment key={product.id}>
        <ListItem 
          alignItems="flex-start"
          sx={{
            bgcolor: 'background.paper',
            mb: 1,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            '&:hover': {
              boxShadow: 1
            }
          }}
          onClick={() => {
            try {
              // Close virtual keyboard and blur any focused element
              try { kb?.close && kb.close(); } catch {}
              try { if (document?.activeElement?.blur) document.activeElement.blur(); } catch {}
              if (hasVariations(product)) {
                toggleProductExpansion(product.id);
              } else {
                toggleCartItem(product, null, null);
              }
            } catch {}
          }}
        >
          <Box sx={{ display: 'flex', width: '100%', alignItems: 'flex-start' }}>
            {/* Product Image */}
            <Box 
              sx={{
                width: 80,
                height: 80,
                minWidth: 80,
                bgcolor: '#f5f5f5',
                borderRadius: 1,
                overflow: 'hidden',
                mr: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                cursor: (product.image || product.images?.[0]?.src) ? 'pointer' : 'default',
                '& img': {
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  padding: '4px',
                  boxSizing: 'border-box'
                }
              }}
              onClick={(e) => {
                e.stopPropagation();
                try { kb?.close && kb.close(); } catch {}
                try { if (document?.activeElement?.blur) document.activeElement.blur(); } catch {}
                const thumb = product.image || product.images?.[0]?.src;
                if (thumb) openImage(product, thumb);
              }}
            >
              {product.image ? (
                <img 
                  src={product.image}
                  alt={product.name}
                  onError={(e) => {
                    console.error('Error loading image:', product.image);
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
              ) : null}
              <Typography 
                variant="caption" 
                color="text.secondary"
                sx={{
                  display: hasImage ? 'none' : 'block',
                  textAlign: 'center',
                  px: 1
                }}
              >
                {hasImage ? 'Image not available' : 'No image'}
              </Typography>
            </Box>

            {/* Product Details */}
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Box sx={{ width: '100%' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.15 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    <Typography 
                      variant="subtitle1"
                      component="div"
                      sx={{
                        fontWeight: 'medium',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        pr: 0,
                        lineHeight: 1.2
                      }}
                    >
                      {product.name}
                    </Typography>
                    {/* Details button removed as requested */}
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                      {getPriceRange(product)}
                    </Typography>
                    {!variations ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, mt: 0.5 }}>
                        <Button
                          variant={isInCart(product) ? 'outlined' : 'contained'}
                          color={isInCart(product) ? 'secondary' : 'primary'}
                          size="small"
                          startIcon={isInCart(product) ? <RemoveIcon /> : <AddIcon />}
                          onClick={(e) => { e.stopPropagation(); try { kb?.close && kb.close(); } catch {} try { if (document?.activeElement?.blur) document.activeElement.blur(); } catch {} toggleCartItem(product, null, e.currentTarget); }}
                          sx={{ 
                            width: 'auto',
                            '&:hover': {
                              bgcolor: isInCart(product) ? 'action.hover' : 'primary.dark'
                            }
                          }}
                        >
                          {isInCart(product) ? 'Remove from Cart' : 'Add to Cart'}
                        </Button>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, mt: 0.5 }}>
                        <Button
                          variant={isInCart(product) ? 'outlined' : 'outlined'}
                          color={isInCart(product) ? 'secondary' : 'primary'}
                          size="small"
                          endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          onClick={(e) => { e.stopPropagation(); try { kb?.close && kb.close(); } catch {} try { if (document?.activeElement?.blur) document.activeElement.blur(); } catch {} toggleProductExpansion(product.id); }}
                          sx={{ 
                            width: 'auto',
                            borderColor: 'primary.main',
                            color: 'primary.main',
                            '&:hover': {
                              borderColor: 'primary.dark',
                              backgroundColor: 'action.hover'
                            }
                          }}
                        >
                          {isInCart(product) ? 'Remove from Cart' : isExpanded ? 'Hide Options' : 'Show Options'}
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Box>
                {/* Left-aligned product category (same indentation as name) */}
                <Box sx={{ mt: 0.5 }}>
                  <Chip 
                    label={getProductCategory(product)}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </ListItem>
        
        {variations && (
          <Box sx={{ pl: 4, pr: 2 }}>
            {renderVariations(product)}
          </Box>
        )}
      </React.Fragment>
    );
  };

  // Render products list
  const renderProducts = () => {
    const productsToShow = displayedProducts;

    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Container maxWidth="lg" sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="textSecondary" gutterBottom>
            {error}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleRefresh}
            startIcon={<CachedIcon />}
            disabled={refreshing}
            sx={{ mt: 2 }}
          >
            {refreshing ? 'Refreshing...' : 'Refresh Page'}
          </Button>
        </Container>
      );
    }

    if (searchTerm && products.length === 0) {
      return (
        <Box p={3} textAlign="center">
          <Typography>No products found matching your search</Typography>
        </Box>
      );
    }
    
    if (!searchTerm) {
      return null;
    }

    return (
      <List disablePadding>
        {products.map(product => renderProductItem(product))}
      </List>
    );
  };

  // Keep the floating checkout button visibility in sync with cart contents
  useEffect(() => {
    setShowCheckoutButton(totalItems > 0);
  }, [totalItems]);

  // Render main content
  return (
    <PageLayout contentMaxWidth="xl">
      <Box sx={{ position: 'relative', pb: showCheckoutButton ? 12 : 0 }}>
      {/* Top action buttons: Back (left), Home (center), Cart (right) */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, position: 'relative', zIndex: (theme) => theme.zIndex.appBar }}>
        <Box sx={{ flex: '0 0 auto' }}>
          <Button 
            variant="outlined" 
            startIcon={<ArrowBackIcon />} 
            onClick={() => { 
              console.log('[Shop] Back clicked -> /home (hard redirect)');
              kb?.close && kb.close();
              window.location.assign('/home');
            }} 
            size="large"
            sx={{
              minWidth: 200,
              px: 3,
              py: 1.2,
              fontSize: '1rem',
              fontWeight: 600,
              backgroundColor: '#e1bee7',
              borderColor: '#b39ddb',
              color: '#4a148c',
              borderWidth: 1.5,
              borderRadius: 1.5,
              '&:hover': {
                backgroundColor: '#d1c4e9',
                borderColor: '#b39ddb'
              }
            }}
          >
            Back
          </Button>
        </Box>
        <Box sx={{ flex: '1 1 auto', display: 'flex', justifyContent: 'center' }}>
          <Button 
            variant="outlined" 
            startIcon={<HomeIcon />} 
            onClick={() => { 
              console.log('[Shop] Home clicked -> /home (hard redirect)');
              kb?.close && kb.close(); 
              window.location.assign('/home');
            }} 
            size="large"
            sx={{
              minWidth: 200,
              px: 3,
              py: 1.2,
              fontSize: '1rem',
              fontWeight: 600,
              backgroundColor: '#e1bee7',
              borderColor: '#b39ddb',
              color: '#4a148c',
              borderWidth: 1.5,
              borderRadius: 1.5,
              '&:hover': {
                backgroundColor: '#d1c4e9',
                borderColor: '#b39ddb'
              }
            }}
          >
            Home
          </Button>
        </Box>
        <Box sx={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button 
            variant="outlined" 
            onClick={() => { kb?.close && kb.close(); navigate('/cart', { state: { from: 'products' } }); }} 
            startIcon={
              <Badge badgeContent={totalItems} color="primary">
                <ShoppingCartIcon />
              </Badge>
            } 
            size="large"
            sx={{
              minWidth: 200,
              px: 3,
              py: 1.2,
              fontSize: '1rem',
              fontWeight: 600,
              backgroundColor: '#e1bee7',
              borderColor: '#b39ddb',
              color: '#4a148c',
              borderWidth: 1.5,
              borderRadius: 1.5,
              '&:hover': {
                backgroundColor: '#d1c4e9',
                borderColor: '#b39ddb'
              }
            }}
          >
            Cart
          </Button>
          {/* Inline Checkout button removed; rely on floating mid-right Checkout FAB */}
        </Box>
      </Box>

      {/* Search Box */}
      <Paper elevation={0} sx={{ p: 3, bgcolor: '#f3e5f5', border: '1px solid #e1bee7', borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom>Search donations, events & services</Typography>
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search donations, events and services..."
            value={searchTerm}
            onChange={handleSearchChange}
            onFocus={openSearchKeyboard}
            onClick={openSearchKeyboard}
            inputProps={{ id: 'shop-search-box-input' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" sx={{ fontSize: '2rem' }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="backspace"
                    edge="end"
                    size="small"
                    onClick={() => {
                      setSearchTerm((prev) => {
                        const next = (prev || '').slice(0, -1);
                        if (next.trim()) {
                          debouncedSearchRef(next);
                        } else {
                          fetchProducts('');
                        }
                        return next;
                      });
                    }}
                    disabled={!searchTerm}
                    sx={{ mr: 0.5 }}
                  >
                    <BackspaceIcon sx={{ fontSize: '2rem' }} />
                  </IconButton>
                  <IconButton
                    aria-label="clear search"
                    edge="end"
                    size="small"
                    onClick={() => {
                      setSearchTerm('');
                      fetchProducts('');
                    }}
                    disabled={!searchTerm}
                  >
                    <ClearIcon sx={{ fontSize: '2rem' }} />
                  </IconButton>
                </InputAdornment>
              ),
              readOnly: !kb?.keyboardDisabled, // Only readonly when virtual keyboard is enabled
              sx: { 
                bgcolor: '#ffffff',
                '& .MuiInputBase-input': {
                  fontSize: '2rem',
                  lineHeight: 1.4,
                  paddingTop: 1.25,
                  paddingBottom: 1.25
                }
              }
            }}
          />
        </Box>

        {/* Search Results directly under search box */}
        {renderProducts()}
        {/* Quick Select (upcoming calendar events) */}
        {renderPrimePicks()}
        {/* Quick Add (env-configured products) */}
        {renderQuickAdd()}

      {/* Image Dialog */}
      <Dialog open={imageDialog.open} onClose={closeImage} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>{imageDialog.title}</Typography>
            {imageDialog.product && imageDialog.product.type !== 'variable' && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={() => { try { kb?.close && kb.close(); } catch {} handleAddSimpleFromImage(imageDialog.product); }}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                Add to Cart · {formatPrice(parseFloat(imageDialog.product.price) || 0)}
              </Button>
            )}
            {imageDialog.product && imageDialog.product.type === 'variable' && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={() => { try { kb?.close && kb.close(); } catch {} openVariationDialog(imageDialog.product, null); }}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                Show Options
              </Button>
            )}
          </Box>
          <IconButton onClick={closeImage} aria-label="close image dialog"
            sx={{
              ml: 1,
              width: 32,
              height: 32,
              borderRadius: '50%',
              bgcolor: '#ff4d4f',
              color: 'white',
              '&:hover': { bgcolor: '#e53935' }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#fff' }}>
          {imageDialog.src ? (
            <img
              src={imageDialog.src}
              alt={imageDialog.title}
              style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">Image not available</Typography>
          )}
        </DialogContent>
      </Dialog>

      {/* Details Dialog */}
      <Dialog
        open={detailsDialog.open}
        onClose={closeDetails}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{ 
          sx: { 
            borderRadius: 2, 
            maxHeight: '63vh', 
            overflowY: 'auto',
            // Always-show, styled scrollbar for better kiosk UX
            '&::-webkit-scrollbar': { width: 10 },
            '&::-webkit-scrollbar-track': { backgroundColor: '#f1f1f1', borderRadius: 8 },
            '&::-webkit-scrollbar-thumb': { backgroundColor: '#c1c1c1', borderRadius: 8 },
            '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#a1a1a1' }
          } 
        }}
      >
        <DialogTitle sx={{ pr: 5 }}>
          {detailsDialog.title}
          <IconButton
            aria-label="close"
            onClick={closeDetails}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              width: 32,
              height: 32,
              borderRadius: '50%',
              bgcolor: '#ff4d4f',
              color: 'white',
              '&:hover': { bgcolor: '#e53935' }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent 
          dividers 
          sx={{ 
            p: 0, 
            position: 'relative',
            // Let Paper handle scroll so the scrollbar appears on the dialog component
            overflow: 'visible'
          }}
        >
          {detailsDialog.url ? (
            <Box sx={{ position: 'relative', minHeight: '120vh' }}>
              {/* Loading overlay */}
              {detailsDialog.loading && (
                <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', zIndex: 1, bgcolor: 'rgba(255,255,255,0.85)' }}>
                  <Box sx={{ width: 42, height: 42, border: '4px solid #e2e8f0', borderTopColor: 'primary.main', borderRadius: '50%', animation: 'spin 1s linear infinite', mb: 1.5 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>Please wait...</Typography>
                </Box>
              )}
              <style>{`@keyframes spin { from { transform: rotate(0deg);} to { transform: rotate(360deg);} }`}</style>
              <iframe
                src={detailsDialog.url}
                title={detailsDialog.title}
                onLoad={() => setDetailsDialog(prev => ({ ...prev, loading: false }))}
                onError={() => setDetailsDialog(prev => ({ ...prev, loading: false }))}
                sandbox="allow-scripts"
                ref={detailsIframeRef}
                onFocus={() => { try { detailsIframeRef.current && detailsIframeRef.current.blur(); } catch {} }}
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0, pointerEvents: 'none' }}
              />

              {/* Bottom label bar only (no full overlay) to preserve scrollbar usability */}
              <Box
                sx={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 2,
                  pointerEvents: 'none',
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    px: 2,
                    py: 1.5,
                    color: 'text.primary',
                    background: 'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.92) 45%, rgba(255,255,255,1) 100%)',
                    textAlign: 'center',
                    fontWeight: 700
                  }}
                >
                  {detailsDialog.title} Details
                </Box>
              </Box>
            </Box>
          ) : (
            <Box p={2}>
              <Typography variant="body2">Link not available.</Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
      </Paper>

      {/* Floating Checkout button (middle-right) when cart has items */}
      {Array.isArray(cart) && cart.length > 0 && (
        <Box
          sx={{
            position: 'fixed',
            top: '50%',
            right: 24,
            transform: 'translateY(-50%)',
            zIndex: (theme) => theme.zIndex.appBar + 1,
          }}
        >
          <Fab
            color="success"
            variant="extended"
            onClick={() => {
              try { kb?.close && kb.close(); } catch {}
              try { if (document?.activeElement?.blur) document.activeElement.blur(); } catch {}
              navigate('/checkout', { state: { from: 'products' } });
            }}
            sx={{
              borderRadius: 999,
              px: 3.5,
              py: 1.5,
              minHeight: 64,
              textTransform: 'none',
              fontWeight: 800,
              fontSize: '1.15rem',
              letterSpacing: 0.2,
              bgcolor: 'success.main',
              color: 'common.white',
              boxShadow: '0 0 0 4px rgba(255,255,255,0.85), 0 10px 20px rgba(0,0,0,0.25)',
              '&:hover': {
                bgcolor: 'success.dark',
                boxShadow: '0 0 0 4px rgba(255,255,255,0.9), 0 14px 28px rgba(0,0,0,0.3)'
              }
            }}
          >
            <ShoppingCartIcon sx={{ mr: 1.25, fontSize: 28 }} />
            {`Checkout (${totalItems}) · ${formatPrice(totalAmount)}`}
          </Fab>
        </Box>
      )}

      {/* Checkout popover removed in favor of floating FAB */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Variation Dialog for Quick Add */}
      <ProductVariationDialog
        open={variationDialogOpen}
        onClose={closeVariationDialog}
        product={dialogProduct}
        onAddToCart={onDialogAddToCart}
        onRemoveFromCart={(cartItemId) => removeFromCart(cartItemId)}
        cart={cart}
      />

      {/* Floating Checkout Button removed in favor of inline placement under Quick Select */}
      </Box>
    </PageLayout>
  );
};

export default ProductsPage;
